import { MuiButton } from '@/components';

import { UseFirstRender } from '@/hooks';
import { actualStory, deleteNotification, loginUser, loginUserView, setStep } from '@/store/actions';
import { inviteAccepted, setGuest } from '@/store/collaborator/action';
import { authSelector, collaboratorSelector, intermitenceSelector } from '@/store/selectors';
import { palette } from '@/theme/constants';
import { Box, Grid, IconButton, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormikConfig } from './formik';
import MuiTextField3 from '@/components/TextField3';
import { useAuth } from '@/hooks/authContext';

export const Login = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const authData = useSelector(authSelector);
  const { loading } = useSelector(intermitenceSelector);
  const dispatch = useDispatch();
  const collaborator = useSelector(collaboratorSelector);
  const { redirectTo } = useAuth();
  const decodeBase64 = (encodedString: any) => {
    return Buffer.from(encodedString, 'base64').toString('utf-8');
  };
  const [decodedUrl, setDecodedUrl] = useState({
    story: decodeBase64(
      Array.isArray(router?.query?.story_id) ? router.query?.story_id[0] : router.query?.story_id || '',
    ),
    email: decodeBase64(Array.isArray(router?.query?.guest) ? router.query?.guest[0] : router.query?.guest || ''),
    role: decodeBase64(Array.isArray(router?.query?.role) ? router.query?.role[0] : router?.query?.role || ''),
    notification: decodeBase64(
      Array.isArray(router?.query?.notification) ? router.query?.notification[0] : router.query?.notification || '',
    ),
    type: decodeBase64(Array.isArray(router?.query?.type) ? router.query?.type[0] : router.query?.type || ''),
  });
  const handleSubmit = (data: any) => {
    data.email = data.email.toLowerCase();
    dispatch(loginUser(data));
  };
  const handleOnTouched = (key: string) => setTouched({ ...touched, [key]: true });
  const [showPassword, setShowPassword] = useState(false);
  const handleShowPassword = () => setShowPassword((show) => !show);
  const {
    values,
    handleSubmit: formikSubmit,
    handleChange,
    errors,
    touched,
    setTouched,
    dirty,
    isValid,
  } = FormikConfig(handleSubmit);

  const changeInputStatus = (value: string, error: any) => {
    if (value !== '') {
      if (error) return 'error';
      return 'inherit';
    }
    return 'inherit';
  };
  const useBodyBackground = (color: string = '#fff') => {
    useEffect(() => {
      document.body.style.background = color;

      return () => {
        document.body.style.background = '';
      };
    }, [color]);
  };
  useBodyBackground(isMobile ? '#F1F7FA' : 'linear-gradient(87.36deg, #D8E8F6 2.61%, #BBDFFF 98.24%)');

  useEffect(() => {
    dispatch(setStep(1));
    if (authData?.isAuth) {
      if (router.asPath?.includes('guest')) {
        if (decodedUrl?.email == authData?.user?.email) {
          dispatch(inviteAccepted({ story_id: decodedUrl?.story, role_name: decodedUrl?.role }));
        } else {
          router.push(redirectTo || '/app/home');
        }
      } else {
        router.push(redirectTo || '/app/home');
      }
    }
  }, [authData?.isAuth]);

  useEffect(() => {
    if (authData?.isAuth && collaborator.roleUser.length > 0) {
      if (collaborator.roleUser !== 'inactive') {
        dispatch(actualStory(decodedUrl.story));
        dispatch(setGuest(decodedUrl?.role));
        if (decodedUrl?.notification.length > 0 && collaborator.roleUser !== 'collaborating')
          dispatch(deleteNotification(decodedUrl?.notification));
        if (decodedUrl?.notification.length > 0 && collaborator.roleUser !== 'collaborating')
          dispatch(deleteNotification(decodedUrl?.notification));
        if (decodedUrl?.role === 'Story_Viewer') {
          router.push(`/app/story/${decodedUrl?.story}`);
        } else {
          router.push(`/app/story/${decodedUrl?.story}/memory/create`);
        }
      }
      if (authData?.isAuth && collaborator?.roleUser === 'inactive') {
        router.push('/app/home');
      }
    }
  }, [collaborator?.roleUser]);

  UseFirstRender(() => {
    if (!authData?.isAuth) {
      dispatch(loginUserView());
    }
  }, [authData?.isAuth]);

  useEffect(() => {
    const referralCode = router?.query?.referral_code;
    if (referralCode) {
      localStorage.setItem('referral_code', referralCode as string);
    }
  }, [router.query]);

  return (
    <>
      <Box
        display={'flex'}
        flexDirection={'column'}
        gap={'1rem'}
        height={'100vh'}
        width={'100%'}
        zIndex={1}
        position={'relative'}
        paddingTop={isMobile ? '46px' : '1rem'}
        justifyContent={!isMobile ? 'center' : 'top'}
        alignItems={!isMobile ? 'center' : ''}
        overflow={'auto'}>
        <Box width={'1200px'} maxWidth={'100%'} margin={'0 auto'}>
          <IconButton
            disableRipple
            onClick={(e) => router.push('/')}
            sx={{ position: 'absolute', top: '1rem', left: isMobile ? '1rem' : '20rem' }}>
            <Image width={24} height={24} alt='back' color='black' src='/icons/left-arrow-black.svg' />
            {!isMobile && (
              <Typography fontSize={'1rem'} color='#212d6d' marginLeft={'0.6rem'}>
                Back to Memvy.com
              </Typography>
            )}
          </IconButton>
          {!isMobile && (
            <Image
              src={isMobile ? '/images/thread_mobile.svg' : '/images/thread.svg'}
              style={{
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                left: 0,
                margin: 'auto',
                zIndex: -1,
              }}
              alt='thread'
              fill
              priority
              sizes='100%'
              quality={80}
            />
          )}

          <Box
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'flex-start'}
            alignItems={'center'}
            width={'100%'}
            maxWidth={'33rem'}
            borderRadius={'0.5rem'}
            bgcolor={'#F1F7FA'}
            margin={'0 auto'}
            padding={isMobile ? '1rem' : '2rem'}>
            <Box marginBottom={'1rem'} position={'relative'}>
              <Image src={`/images/logo-new.svg`} alt={'logo'} width={66} height={34} quality={80} />
              <Box display={'flex'} justifyContent={'flex-start'} width={'100%'}>
                <Typography textAlign={'center'} fontWeight={400} color='#1D1D1D' marginTop={'1rem'} variant='h4'>
                  Log In
                </Typography>
              </Box>
            </Box>

            <form onSubmit={formikSubmit}>
              <Grid container width={'100%'} marginBottom={'0.5rem'} rowSpacing={2}>
                <Grid item xs={12} mb={'1.25rem'}>
                  <Typography align='left' color={palette?.background} mb={'0.5rem'} fontSize={'1rem'}>
                    Email Address
                  </Typography>
                  <MuiTextField3
                    id='email'
                    name='email'
                    fullWidth
                    onBlur={() => {
                      handleOnTouched('email');
                    }}
                    status={changeInputStatus(values.email, errors.email && touched.email)}
                    onChange={handleChange}
                    value={values.email}
                    autoComplete='email'
                    placeholder={t('email')}
                    errorMessage={errors.email}
                  />
                </Grid>
                <Grid item xs={12} mb={'1.25rem'}>
                  <Typography align='left' color={palette?.background} mb={'0.5rem'} fontSize={'1rem'}>
                    Password
                  </Typography>
                  <MuiTextField3
                    id='password'
                    name='password'
                    fullWidth
                    onBlur={() => {
                      handleOnTouched('password');
                    }}
                    onChange={handleChange}
                    value={values.password}
                    autoComplete='current-password'
                    placeholder={t('password')}
                    iconMethod={handleShowPassword}
                    iconHeight={24}
                    iconWidth={24}
                    type={!showPassword ? 'password' : 'text'}
                    endIcon={showPassword ? '/icons/eye-blue.svg' : '/icons/eye-light.svg'}
                    errorMessage={errors.password}
                    status={changeInputStatus(values.password, errors.password && touched.password)}
                  />
                </Grid>

                <Grid item textAlign={'center'} xs={12} mb={'1.25rem'}>
                  <Link href={'/app/forgot-password'}>
                    <Typography sx={{ textDecoration: 'underline' }} fontSize={'1rem'} color={palette.primary}>
                      {t('forgot_password')}
                    </Typography>
                  </Link>
                </Grid>
                <Grid item xs={12}>
                  <MuiButton
                    sx={{
                      '&:disabled': {
                        backgroundColor: '#B0B3C0',
                        color: '#FFFFFF',
                        opacity: 1,
                      },
                      height: '60px',
                      borderRadius: '0.6rem',
                      fontSize: '1rem',
                      backgroundColor: !isValid ? '#B0B3C0' : '#212D6D',
                    }}
                    type='submit'
                    disabled={!isValid || !dirty || loading}
                    loading={loading}
                    variant={'contained'}>
                    <Typography variant='button'>{t('login')}</Typography>
                  </MuiButton>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Box>
    </>
  );
};
